import type { BaseArea } from 'services/assist-api/entities/area/areas';
import { useArea } from 'services/assist-api/entities/area/areas';
import { useCustomerUsers } from 'services/assist-api/entities/customer-user/customer-users';
import { useCustomerAreaUsers } from 'services/assist-api/entities/customers-areas-user/customers-areas-users';
import { useCustomerAreas } from 'services/assist-api/entities/customers-areas/customers-areas';
import type { ROLE } from 'services/assist-api/entities/role/roles';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { useAuth } from 'services/providers/auth.provider';
import { isInitialLoad } from 'utils/check-query-status';

export default function useCurrentAreaAccess(areaId: number): {
  currentArea: BaseArea | undefined;
  currentAreaRoles: ROLE[];
  belongsToCurrentArea: boolean;
  isCurrentAreaAccessLoading: boolean;
} {
  // Fetch current area:
  const areaQuery = useArea(areaId);
  const currentArea = areaQuery.data?.json;

  // Fetch current area roles:
  const { me } = useAuth();
  const currentOrganisation = useAuthCustomer();

  const organisationUsersQuery = useCustomerUsers({
    filters: {
      user: me?.id,
      customer: currentOrganisation?.id
    }
  });
  const organisationUserId = organisationUsersQuery.data?.json?.[0]?.id;

  const organisationAreasQuery = useCustomerAreas({
    filters: {
      area: areaId,
      customer: currentOrganisation?.id
    }
  });
  const organisationAreaId = organisationAreasQuery.data?.json?.[0]?.id;

  const organisationAreaUsersQuery = useCustomerAreaUsers({
    filters: {
      user: organisationUserId,
      area: organisationAreaId
    }
  });
  const currentAreaRoles =
    organisationAreaUsersQuery.data?.json[0]?.roles?.map((r) => r.name) || [];
  const belongsToCurrentArea = (organisationAreaUsersQuery.data?.json.length || 0) > 0;

  const isCurrentAreaAccessLoading = isInitialLoad(
    areaQuery,
    organisationUsersQuery,
    organisationAreasQuery,
    organisationAreaUsersQuery
  );

  return {
    currentArea,
    currentAreaRoles,
    belongsToCurrentArea,
    isCurrentAreaAccessLoading
  };
}
